import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    CircularProgress,
    Divider,
    Stack
} from "@mui/material";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {ArrowDownward} from "@mui/icons-material";
import * as React from "react";
import {useEffect, useState} from "react";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {Media} from "crqlar-react-common/dist/models/common/media.model";

interface Props {
    agenda: AgendaV3
    setAgenda: (item: AgendaV3) => void
}

const stylingCss = `
/* ===== Container structure ===== */

/* Root holder of the agenda widget */
.agenda {}

/* Header wrapper above the scheduler (logo, title) */
.agenda__header {}

/* Optional hero/logo image */
.agenda__logo {}

/* Main title text */
.agenda__title {}

/* Main content area that contains the Mobiscroll scheduler */
.agenda__main {}


/* ===== Event item inside Mobiscroll ===== */

/* Clickable event row wrapper */
.agenda-event {}

/* Colored bar shown on the left of each event */
.agenda-event__bar {}

/* Event content wrapper (title + description) */
.agenda-event__content {}

/* Event title text */
.agenda-event__title {}

/* Event description text (e.g., bookings) */
.agenda-event__desc {}


/* ===== CSS Variables (set elsewhere; listed here for reference) =====
   --agenda-primary
   --agenda-on-primary
   --agenda-panel-bg
   --agenda-font
*/


/* ===== Optional Mobiscroll override hooks (keep empty, override if needed) ===== */

/* Calendar background panel */
.mbsc-calendar-wrapper {}

/* Selected day cell content */
.mbsc-ios .mbsc-selected .mbsc-calendar-cell-text {}

/* iOS flat buttons (e.g., text buttons) */
.mbsc-ios .mbsc-button.mbsc-button-flat {}
`;


export interface RemoteFont {
    url: string;
    family: string;
}



const DEFAULT_FONT: RemoteFont = {
    url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap",
    family: `"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`,
};

const isValidUrl = (v: string) => /^https?:\/\//i.test(v);
const isGoogleFontsUrl = (v: string) => /fonts\.googleapis\.com/i.test(v);

// grab first family=... from the URL, turn + into spaces, drop extras
const extractGoogleFamily = (u: string) => {
    try {
        const url = new URL(u);
        const famParams = url.searchParams.getAll("family");
        if (!famParams.length) return undefined;
        const first = famParams[0].split(":")[0].replace(/\+/g, " ").trim();
        if (!first) return undefined;
        return `"${first}", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    } catch {
        return undefined;
    }
};


const AgendaDetailsStylingTab: React.FC<Props> = ({
                                                      agenda,
                                                      setAgenda
                                                  }) => {
    const {t} = useLocalize()

    const updateUiColor = (
        field: 'primaryColor' | 'secondaryColor' | 'surfaceColor' | 'backgroundColor',
        value?: string
    ) => {
        setAgenda({
            ...agenda,
            uiConfig: {
                ...agenda.uiConfig,
                [field]: value,
            },
        });
    }

    const imageSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Hero Image')}
            </Typography>

            <Stack gap={1}>
                <Alert
                    variant={'outlined'}
                    severity={'info'}
                    // Match the width of the landscape upload field below (230px height at 16:9)
                    sx={{py: 0, lineHeight: 1, alignItems: 'center', maxWidth: 230 * 16 / 9}}
                >
                    {t('An optional hero image will be displayed at the top and span the full width.')}
                </Alert>

                <ImageUploadField
                    id="image-picker"
                    image={agenda.uiConfig?.logo}
                    setImage={(image) => {
                        setAgenda({
                            ...agenda,
                            uiConfig: { ...agenda.uiConfig, logo: image },
                        });
                    }}
                    ratio={'landscape'}
                />
            </Stack>
        </Stack>
    )

    const stylingSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Styling')}
            </Typography>

            <Stack gap={1}>
            <Property
                title={t('Primary color')}
                tooltip={t('Main accent color used for agenda actions and highlighted elements.')}
            >
                <NewColorPicker
                    value={agenda.uiConfig?.primaryColor ?? CommonThemeColors.sys.primary.main}
                    isRequired
                    onChange={(newColor) => updateUiColor('primaryColor', newColor)}
                />
            </Property>

            {/* Secondary color */}
            <Property
                title={t('Secondary color')}
                tooltip={t('Secondary accent color used for supporting agenda UI elements.')}
            >
                <NewColorPicker
                    value={agenda.uiConfig?.secondaryColor ?? CommonThemeColors.sys.secondary.main}
                    isRequired
                    onChange={(newColor) => updateUiColor('secondaryColor', newColor)}
                />
            </Property>

            {/* Surface color */}
            <Property
                title={t('Surface color')}
                tooltip={t('Background color for agenda panels and content surfaces.')}
            >
                <NewColorPicker
                    value={agenda.uiConfig?.surfaceColor ?? "#fff"}
                    isRequired
                    onChange={(newColor) => updateUiColor('surfaceColor', newColor)}
                />
            </Property>

            {/* Background color */}
            <Property
                title={t('Background color')}
                tooltip={t('Page background color behind the agenda content.')}
            >
                <NewColorPicker
                    value={agenda.uiConfig?.backgroundColor ?? "#fff"}
                    isRequired
                    onChange={(newColor) => updateUiColor('backgroundColor', newColor)}
                />
            </Property>

            <Property
                title={t('Custom CSS')}
                tooltip={t('Advanced CSS overrides applied to this agenda widget.')}
            >
                <Box sx={{width: "100%"}}>
                    <TextField
                        multiline
                        rows={(window.innerHeight - 300) / 60}
                        value={agenda.uiConfig?.customCSS}
                        onChange={(e) => setAgenda({
                            ...agenda,
                            uiConfig: {
                                ...agenda.uiConfig,
                                customCSS: e.target.value
                            }
                        })}
                        fullWidth
                    />

                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ArrowDownward/>}
                        >
                            {t('All custom styling CSS options')}
                        </AccordionSummary>

                        <AccordionDetails>
                            <Box
                                sx={{
                                    width: '100%',
                                    position: 'relative',
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {stylingCss}
                            </Box>
                        </AccordionDetails>

                    </Accordion>
                </Box>
            </Property>
            </Stack>
        </Stack>
    )
    const [fontUrl, setFontUrl] = useState<string>(agenda.uiConfig?.font?.font?.mediaURI || DEFAULT_FONT.url);
    const [fontFamily, setFontFamily] = useState<string>(agenda.uiConfig?.font?.family || DEFAULT_FONT.family);
    const [loadingName, setLoadingName] = useState(false);
    const [editName, setEditName] = useState(true);

    useEffect(() => {
        const valid = isValidUrl(fontUrl);
        const isG = isGoogleFontsUrl(fontUrl);
        if (!valid || !isG) {
            setEditName(true);
            return;
        }
        setEditName(false);
        setLoadingName(true);
        const h = window.setTimeout(() => {
            const parsed = extractGoogleFamily(fontUrl);
            if (parsed) setFontFamily(parsed);
            setLoadingName(false);
        }, 200);
        return () => window.clearTimeout(h);
    }, [fontUrl]);

    useEffect(() => {
        setAgenda({
            ...agenda,
            uiConfig: {
                ...agenda.uiConfig,
                font: {
                    font: {mediaURI: fontUrl, provider: 'remote'} as Media,
                    family: fontFamily
                },
            },
        });
        const styleId = 'agenda-font-style';
        const el = (document.getElementById(styleId) as HTMLStyleElement) || (() => {
            const s = document.createElement('style');
            s.id = styleId;
            document.head.appendChild(s);
            return s;
        })();
        el.innerHTML = `@import url('${fontUrl}'); :root { --agenda-font: ${fontFamily}; }`;
    }, [fontUrl, fontFamily]);

    const fontSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Fonts')}
            </Typography>

            <Stack gap={1}>
            <Alert
                variant={'outlined'}
                severity={'info'}
                sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
            >
                {t('The URL must be publicly available (recommended: Google Fonts). The font family name must start with the selected font in "<font-name>" followed by fallback names.')}
            </Alert>

            <Property
                title={t('Font URL')}
                tooltip={t('Public stylesheet URL for the agenda font, usually from Google Fonts.')}
            >
                <TextField
                    size="small"
                    value={fontUrl}
                    onChange={(e) => setFontUrl(e.target.value)}
                    fullWidth
                    InputProps={{
                        endAdornment: loadingName ?
                            <CircularProgress size={18}/> : null,
                    }}
                />
            </Property>

            <Property
                title={t('Font family')}
                tooltip={t('CSS font-family value used by the agenda. For Google Fonts this is detected from the URL.')}
            >
                <Stack direction="row" spacing={1} sx={{width: "100%"}}>
                    <TextField
                        size="small"
                        value={fontFamily}
                        onChange={(e) => setFontFamily(e.target.value)}
                        fullWidth
                        disabled={!editName && isValidUrl(fontUrl) && isGoogleFontsUrl(fontUrl)}
                    />
                    <Button variant="outlined" onClick={() => setEditName(v => !v)}>
                        {editName ? t('Confirm') : t('Edit')}
                    </Button>
                </Stack>
            </Property>

            <Property title={t('Preview')}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        fontFamily: 'var(--agenda-font, ' + fontFamily + ')',
                        width: '100%',
                    }}
                >
                    <Typography variant="subtitle2" sx={{mb: 0.5, opacity: 0.7}}>
                        {t('Preview')}
                    </Typography>
                    <Typography>
                        {t('The quick brown fox jumps over the lazy dog. 0123456789 !?&%$')}
                    </Typography>
                </Box>
            </Property>
            </Stack>
        </Stack>
    );


    return (
        <Stack
            gap={6}
        >
            {imageSection}
            <Divider/>
            {fontSection}
            <Divider/>
            {stylingSection}
        </Stack>
    )
}

export default AgendaDetailsStylingTab;
